import { apiUrl, ApiError, CORRELATION_HEADER, newTraceparent } from './client'
import type { ProblemDetails } from './types'

/**
 * The anonymous end of an invitation. No bearer token: the token in the URL is the
 * credential, and this runs BEFORE Keycloak is initialised (there is no session to
 * carry). Kept apart from `api` so nothing here can ever pick up a token by accident.
 */

export interface InvitationPreview {
  roomName: string
  organizationName: string
  email: string
  expiresUtc: string
  invitedBy: string | null
  message: string | null
  needsAccount: boolean
}

export interface InvitationAccepted {
  roomId: string
  roomName: string
  email: string
  continueUrl: string
}

async function anonymous<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(apiUrl(path), {
    ...init,
    headers: {
      Accept: 'application/json',
      traceparent: newTraceparent(),
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
    },
  })
  const correlationId = response.headers.get(CORRELATION_HEADER)
  const text = await response.text()
  let payload: unknown = undefined
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = { title: text }
    }
  }
  if (!response.ok) {
    throw new ApiError(response.status, (payload ?? {}) as ProblemDetails, correlationId)
  }
  return payload as T
}

export const previewInvitation = (token: string) =>
  anonymous<InvitationPreview>(`/api/invite/${encodeURIComponent(token)}`)

export const acceptInvitation = (token: string, displayName: string, password: string) =>
  anonymous<InvitationAccepted>(`/api/invite/${encodeURIComponent(token)}/accept`, {
    method: 'POST',
    body: JSON.stringify({ displayName, password }),
  })
